/**
 * Error raised for non-2xx API responses (docs/API.md error format {"detail", "message"}). Mapping of status codes
 * to UI behaviour lives here only: 401 -> login, 403 -> hidden/forbidden (FR-RB-01), 404 -> not found (SEC-08).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly serverMessage: string | null;

  constructor(status: number, detail: string, serverMessage: string | null) {
    super(`API request failed with ${status}: ${detail}`);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.serverMessage = serverMessage;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get requiresReauth(): boolean {
    return this.status === 428;
  }
}

/** Network failure before any HTTP status was received. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Network request failed', { cause });
    this.name = 'NetworkError';
  }
}

export function isApiError(candidate: unknown): candidate is ApiError {
  return candidate instanceof ApiError;
}

/** TanStack Query retry policy: never retry client errors, retry network/5xx twice. */
export function shouldRetryRequest(failureCount: number, requestError: unknown): boolean {
  if (isApiError(requestError) && requestError.status < 500) {
    return false;
  }
  return failureCount < 2;
}
